import type { RoutingGuideShortInfo } from '@/views/screens/RoutingGuide/types';
import { convertDateUTCToUserLocal } from '../../utilz/dates';
import { DEFAULT_STORE_OBJECT } from '../constants';
import type { DefaultStoreObject } from '../types';
import RoutingGuideTypes from './RoutingGuideTypes';
import getroutingGuidelistBySearch from './requestPayload/getRoutingGuideList';

export interface RoutingGuideState extends Record<string, any> {
  selectedRoutingGuides: RoutingGuideShortInfo['routingGuideId'][];
  routingGuideDetails: DefaultStoreObject<RoutingGuideShortInfo[]> & {
    req: {
      PageSize: number;
      PageNo: number;
    };
    totalCount: number;
  };
}

const NXP_user_request = {
  ClientCode: '',
  CompanyName: '',
  UserStatus: '',
  UserName: '',
  UserTypeId: '',
  PageNo: 1,
  PageSize: 10,
};

const INITIAL_FILTER_PARAMS = [
  { type: 'CountryCode', value: null },
  { type: 'TransportationGroup', value: null },
  { type: 'TransportationMode', value: null },
  { type: 'UserId', value: null },
  { type: 'CarrierId', value: null },
  { type: 'Email', value: null },
];

const initialState: RoutingGuideState = {
  routingGuideDetails: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
    req: {
      ...getroutingGuidelistBySearch,
    },
    totalCount: 0,
  },
  selectedRoutingGuides: [],
  routingSpotQuoteGuideData: {
    data: {},
  },
  transportData: [],
  createRoutingGuide: {},
  createRoutingGuideUser: {},
  nxpHomeContent: {},
  nxpUpdateHomeContent: {},
  nxpUserSearchList: {
    data: [],
    request: { ...NXP_user_request },
  },
  isNXPUserSelectedAll: false,
  deleteNxpUser: {},
  npxUserTypesData: {
    data: [],
  },
  clientCodeData: [],
  guidesListSearchParams: INITIAL_FILTER_PARAMS,
  routingSpotFilteredData: [],
  routingGuideCarriers: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
  },
  routingGuideCollaborators: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
  },
  createdRoutingGuideId: null,
  allUsersNames: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
  },
};

// biome-ignore lint/style/useDefaultParameterLast: <explanation>
const RoutingGuideReducer = (state = initialState, { type, payload }: any): RoutingGuideState => {
  switch (type) {
    case RoutingGuideTypes.CLIENT_CODE_DATA: {
      return {
        ...state,
        clientCodeData: payload,
      };
    }

    case RoutingGuideTypes.NPX_USER_TYPES_DATA: {
      return {
        ...state,
        npxUserTypesData: {
          data: payload,
        },
      };
    }

    case RoutingGuideTypes.SET_TRASPORT_DATA: {
      return {
        ...state,
        transportData: payload,
      };
    }

    case RoutingGuideTypes.GET_ROUTING_GUIDE_LIST_CLEAR_REQ: {
      return {
        ...state,
        routingGuideDetails: {
          ...DEFAULT_STORE_OBJECT,
          ...state.routingGuideDetails,
          req: {
            ...getroutingGuidelistBySearch,
          },
          data: [],
          progressing: true,
        },
      };
    }

    case RoutingGuideTypes.GET_ROUTING_GUIDE_LIST: {
      return {
        ...state,
        routingGuideDetails: {
          ...DEFAULT_STORE_OBJECT,
          ...state.routingGuideDetails,
          data: [...(state.routingGuideDetails.data || [])],
          req: {
            ...state.routingGuideDetails.req,
            ...payload,
          },
          progressing: true,
        },
      };
    }

    case RoutingGuideTypes.GET_ROUTING_GUIDE_SUCCESS: {
      return {
        ...state,
        routingGuideDetails: {
          ...DEFAULT_STORE_OBJECT,
          ...state.routingGuideDetails,

          data: [
            ...(state.routingGuideDetails.data || []),
            // @ts-ignore
            ...payload.data.routingGuideList.map((element) => ({
              ...element,
              createdOn: convertDateUTCToUserLocal(element.createdOn),
              modifiedOn: convertDateUTCToUserLocal(element.modifiedOn),
            })),
          ],
          progressing: false,
          totalCount: payload.data.totalCount,
          success: true,
        },
      };
    }

    case RoutingGuideTypes.GET_ROUTING_GUIDE_DATA_RESET: {
      return {
        ...state,
        routingGuideDetails: {
          ...DEFAULT_STORE_OBJECT,
          ...state.routingGuideDetails,
          req: {
            ...state.routingGuideDetails.req,
          },
          success: false,
          error: false,
          data: [],
        },
      };
    }

    // HB Deprecated ?
    case RoutingGuideTypes.GET_SPOT_QUOTE_ROUTING_GUIDE_DATA_PROGRESS: {
      return {
        ...state,
        routingSpotQuoteGuideData: {
          ...state.routingSpotQuoteGuideData,
          progressing: true,
        },
      };
    }

    case RoutingGuideTypes.GET_SPOT_QUOTE_ROUTING_GUIDE_SUCCESS: {
      return {
        ...state,
        routingSpotQuoteGuideData: {
          ...state.routingSpotQuoteGuideData,
          success: true,
          progressing: false,
          data: payload.data,
        },
      };
    }

    case RoutingGuideTypes.DELETE_ROUTINGGUIDES_PROGRESS: {
      return {
        ...state,
        deleteRoutingGuides: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
        },
      };
    }

    case RoutingGuideTypes.DELETE_ROUTINGGUIDES_SUCCESS: {
      return {
        ...state,
        deleteRoutingGuides: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }

    case RoutingGuideTypes.DELETE_ROUTINGGUIDES_ERROR: {
      return {
        ...state,
        deleteRoutingGuides: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case RoutingGuideTypes.SET_SELECTED_ROUTING_GUIDE: {
      return {
        ...state,
        selectedRoutingGuides: payload,
      };
    }

    case RoutingGuideTypes.CREATE_ROUTING_GUIDE_PROGRESS: {
      return {
        ...state,
        createRoutingGuide: {
          ...state.createRoutingGuide,
          progressing: true,
        },
      };
    }

    case RoutingGuideTypes.CREATE_ROUTING_GUIDE_SUCCESS: {
      return {
        ...state,
        createRoutingGuide: {
          ...state.createRoutingGuide,
          success: true,
          progressing: false,
          data: payload.data,
        },
      };
    }

    case RoutingGuideTypes.CREATE_ROUTING_GUIDE_CLEAR: {
      return {
        ...state,
        createRoutingGuide: {
          ...state.createRoutingGuide,
          data: {},
          success: false,
          progressing: false,
        },
      };
    }

    case RoutingGuideTypes.CREATE_ROUTING_GUIDE_USER_PROGRESS: {
      return {
        ...state,
        createRoutingGuideUser: {
          ...state.createRoutingGuideUser,
          progressing: true,
        },
      };
    }

    case RoutingGuideTypes.CREATE_ROUTING_GUIDE_USER_SUCCESS: {
      return {
        ...state,
        createRoutingGuideUser: {
          ...state.createRoutingGuideUser,
          success: true,
          progressing: false,
          data: payload.data,
        },
      };
    }

    case RoutingGuideTypes.CREATE_ROUTING_GUIDE_USER_CLEAR: {
      return {
        ...state,
        createRoutingGuideUser: {
          ...state.createRoutingGuideUser,
          data: {},
          success: false,
          progressing: false,
        },
      };
    }

    case RoutingGuideTypes.GET_CONTENT_NXP_ROUTING_GUIDE: {
      return {
        ...state,
        nxpHomeContent: {
          ...state.nxpHomeContent,
          progressing: true,
          data: {},
        },
      };
    }

    case RoutingGuideTypes.GET_CONTENT_NXP_ROUTING_GUIDE_SUCCESS: {
      return {
        ...state,
        nxpHomeContent: {
          ...state.nxpHomeContent,
          success: true,
          progressing: false,
          data: payload.data,
        },
      };
    }

    case RoutingGuideTypes.GET_CONTENT_NXP_ROUTING_GUIDE_ERROR: {
      return {
        ...state,
        nxpHomeContent: {
          ...state.nxpHomeContent,
          data: {},
          success: false,
          progressing: false,
        },
      };
    }

    case RoutingGuideTypes.UPDATE_CONTENT_NXP_ROUTING_GUIDE: {
      return {
        ...state,
        nxpUpdateHomeContent: {
          ...state.nxpUpdateHomeContent,
          progressing: true,
          data: {},
        },
      };
    }

    case RoutingGuideTypes.UPDATE_CONTENT_NXP_ROUTING_GUIDE_SUCCESS: {
      return {
        ...state,
        nxpUpdateHomeContent: {
          ...state.nxpUpdateHomeContent,
          success: true,
          progressing: false,
          data: payload.data,
        },
      };
    }

    case RoutingGuideTypes.UPDATE_CONTENT_NXP_ROUTING_GUIDE_ERROR: {
      return {
        ...state,
        nxpUpdateHomeContent: {
          ...state.nxpUpdateHomeContent,
          data: {},
          success: false,
          progressing: false,
        },
      };
    }

    case RoutingGuideTypes.GET_NXP_USER_SEARCH_INPROGRESS: {
      return {
        ...state,
        nxpUserSearchList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.nxpUserSearchList,
          request: {
            ...state.nxpUserSearchList.request,
            ...payload,
          },
          progressing: true,
        },
      };
    }

    case RoutingGuideTypes.GET_NXP_USER_SEARCH_SUCCESS: {
      // @ts-ignore
      payload?.data?.data?.forEach((element) => {
        element.isChecked = state.isNXPUserSelectedAll;
      });
      return {
        ...state,
        nxpUserSearchList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.nxpUserSearchList,
          // biome-ignore lint/correctness/noUnsafeOptionalChaining: <explanation>
          data: [...state.nxpUserSearchList.data, ...payload?.data?.data],
          progressing: false,
          totalCount: payload?.data?.totalRecords,
          success: true,
          error: false,
        },
      };
    }

    case RoutingGuideTypes.GET_NXP_USER_SEARCH_FAIL: {
      return {
        ...state,
        nxpUserSearchList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.nxpUserSearchList,
          progressing: false,
          success: false,
          error: true,
        },
      };
    }

    case RoutingGuideTypes.GET_NXP_USER_SEARCH_RESET_REQUEST: {
      return {
        ...state,
        nxpUserSearchList: {
          ...state.nxpUserSearchList,
          request: {
            ...NXP_user_request,
          },
        },
      };
    }

    case RoutingGuideTypes.GET_NXP_USER_SEARCH_CLEAR_DATA: {
      return {
        ...state,
        nxpUserSearchList: {
          ...state.nxpUserSearchList,
          data: [],
        },
      };
    }

    case RoutingGuideTypes.GET_NXP_USER_SELECTED_RECORD: {
      let isSelectedAll = false;
      const dataList = [...state.nxpUserSearchList.data];
      if (payload === true || payload === false) {
        isSelectedAll = payload;
        // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
        dataList.forEach((item) => (item.isChecked = payload));
      } else {
        const foundObject = dataList.find((obj) => obj.userId === payload);
        if (foundObject) {
          foundObject.isChecked = !foundObject.isChecked;
        }
        const findChekedItem = dataList.filter((i) => i.isChecked === true);
        if (findChekedItem.length === dataList.length) {
          isSelectedAll = true;
        }
      }

      return {
        ...state,
        isNXPUserSelectedAll: isSelectedAll,
        nxpUserSearchList: {
          ...state.nxpUserSearchList,
          data: dataList,
        },
      };
    }

    case RoutingGuideTypes.DELETE_NXP_USER: {
      return {
        ...state,
        deleteNxpUser: {
          ...state.deleteNxpUser,
          progressing: true,
        },
      };
    }

    case RoutingGuideTypes.DELETE_NXP_USER_SUCCESS: {
      return {
        ...state,
        deleteNxpUser: {
          ...state.deleteNxpUser,
          success: true,
          progressing: false,
          data: payload.data,
        },
      };
    }

    case RoutingGuideTypes.DELETE_NXP_USER_FAIL: {
      return {
        ...state,
        deleteNxpUser: {
          ...state.deleteNxpUser,
          success: false,
          progressing: false,
          data: [],
          error: true,
        },
      };
    }

    case RoutingGuideTypes.SET_ROUTING_GUIDE_SEARCH_PARAMS: {
      let updatedParams = [...state.guidesListSearchParams];
      const shouldOverwrite = updatedParams.some((param) => param.type === payload.type);

      if (shouldOverwrite) {
        updatedParams = updatedParams.map((param) => {
          if (param.type === payload.type) {
            return {
              ...param,
              value: payload.value,
            };
          }
          return param;
        });
      } else {
        updatedParams.push(payload);
      }

      return {
        ...state,
        guidesListSearchParams: updatedParams,
      };
    }
    case RoutingGuideTypes.RESET_ROUTING_GUIDE_SEARCH_PARAMS: {
      return {
        ...state,
        guidesListSearchParams: [],
      };
    }

    case RoutingGuideTypes.SET_ROUTING_SPOT_FILTERED_DATA: {
      return {
        ...state,
        routingSpotFilteredData: payload,
      };
    }

    case RoutingGuideTypes.GET_CARRIERS_ROUTING_GUIDE_PROGRESS: {
      return {
        ...state,
        routingGuideCarriers: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
        },
      };
    }

    case RoutingGuideTypes.GET_COLLABORATORS_ROUTING_GUIDE_PROGRESS: {
      return {
        ...state,
        routingGuideCollaborators: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
        },
      };
    }

    case RoutingGuideTypes.GET_CARRIERS_ROUTING_GUIDE_SUCCESS: {
      return {
        ...state,
        routingGuideCarriers: {
          ...state.routingGuideCarriers,
          progressing: false,
          success: true,
          ...payload,
        },
      };
    }

    case RoutingGuideTypes.GET_COLLABORATORS_ROUTING_GUIDE_SUCCESS: {
      return {
        ...state,
        routingGuideCollaborators: {
          ...state.routingGuideCollaborators,
          progressing: false,
          success: true,
          ...payload,
        },
      };
    }

    case RoutingGuideTypes.GET_ALL_USERS_NAME_SUCCESS: {
      return {
        ...state,
        allUsersNames: {
          ...state.allUsersNames,
          progressing: false,
          success: true,
          ...payload,
        },
      };
    }

    case RoutingGuideTypes.GET_ALL_USERS_NAME_PROGRESS: {
      return {
        ...state,
        allUsersNames: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
        },
      };
    }

    case RoutingGuideTypes.GET_CARRIERS_ROUTING_GUIDE_ERROR: {
      return {
        ...state,
        routingGuideCarriers: {
          ...state.routingGuideCarriers,
          progressing: false,
          error: true,
          data: [],
          ...payload,
        },
      };
    }

    case RoutingGuideTypes.ADD_ROUTING_GUIDE_HEADER: {
      return {
        ...state,
        createdRoutingGuideId: payload,
      };
    }

    default:
      return state;
  }
};
export default RoutingGuideReducer;
